import { exampleCommands } from "@/lib/examples";
import { buildQueryString, fuseBaseServiceUrl } from "@/services/endpoint-urls";
import type { MondayBoardContext } from "@/services/monday";

// Purpose: Service to call our CLI backend.

export type ExecutionId = {
  id: string;
};

export type Command = {
  id: string;
  category: string;
  shortDescription: string;
  longDescription: string;
  text: string;
  note: string;
};

// Interfaces that specifies the CLI service made of a single method telling how to run a command.
export interface RunCliService {
  runCommand: (
    command: string,
    requestId: string,
    context: MondayBoardContext,
    sessionToken: string
  ) => Promise<Response>;
  pollCommand: (requestId: string, context: MondayBoardContext, sessionToken: string) => Promise<PollResult>;
  exampleCommands: Command[];
}

export const cliService: RunCliService = {
  runCommand,
  pollCommand,
  exampleCommands,
};

export interface ResultType {
  duration: number;
  actionId: string;
  output: string;
  exitCode: ExitCode;
}

// Manually derived from com.appfire.common.cli.ExitCode
export enum ExitCode {
  SUCCESS = "SUCCESS",
  CLIENT_EXCEPTION = "CLIENT_EXCEPTION",
  PARAMETER_PROBLEM = "PARAMETER_PROBLEM",
  HELP = "HELP",
  REMOTE_EXCEPTION = "REMOTE_EXCEPTION",
  CLIENT_SPECIFIC_EXCEPTION = "CLIENT_SPECIFIC_EXCEPTION",
  SQL_EXCEPTION = "SQL_EXCEPTION",
  FAILURE = "FAILURE",
}

export interface ActionResult {
  start: number;
  duration: number;
  results: Array<ResultType>;
  exitCode?: ExitCode;
  accountId?: string;
  automationId?: string;
  automationTitle?: string;
  trigger?: string;
}

export type RunResult = string;

export interface PollResult {
  retry: boolean;
  success?: boolean;
  duration?: number;
  results?: Array<ResultType>;
  exitCode?: ExitCode;
  failureReason?: string;
}

export async function runCommand(
  command: string,
  requestId: string,
  context: MondayBoardContext,
  sessionToken: string
): Promise<Response> {
  const fuseBaseUrl = fuseBaseServiceUrl();
  const response = await fetch(`${fuseBaseUrl}/sharedapi/automation/1.1/run?productType=monday`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": sessionToken,
    },
    body: JSON.stringify({
      automation: {
        locale: "en",
        resultsUrl: requestId,
        actions: [
          {
            id: "monday-run",
            parms: {
              input: command.trim(),
            },
          },
        ],
      },
      context,
    }),
  });

  if (!response.ok) {
    console.error(response.statusText || response.status);
    throw Error(response.statusText || response.status.toString());
  }

  return response;
}

async function pollCommand(requestId: string, _context: MondayBoardContext, sessionToken: string): Promise<PollResult> {
  const fuseBaseUrl = fuseBaseServiceUrl();
  console.debug(`Polling ${requestId}`);
  const queryString = buildQueryString({
    request_id: requestId,
    // https://appfire.atlassian.net/browse/CONCOM-3101
    productType: "monday",
  });
  const resultsUrl = `${fuseBaseUrl}/api/automationresults?${queryString}`;
  const response = await fetch(resultsUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": sessionToken,
    },
  });
  // TODO: Generally this needs much more work...
  // The observed behavior of ac-automation does not match that expected from documentation or conversation.
  // The following is based observed behavior.
  if (response.ok) {
    // TODO: Better marshalling solution / get rid of type assertion
    const data = (await response.json()) as ActionResult;
    // A null exit code or lack of results generally means the results are pending
    if (!data.exitCode || !data.results) {
      return { retry: true };
    }
    // An exit code & results generally means processing is complete
    else if (data.exitCode && data.results) {
      return { retry: false, success: true, duration: data.duration, exitCode: data.exitCode, results: data.results };
    }
    // TODO: Can this happen?
    else {
      return { retry: true };
    }
  } else if (response.status === 404) {
    // A 404 status also generally means the results are pending
    return { retry: true };
  } else {
    // Otherwise, the response indicates some kind of error, probably...
    return { retry: false, success: false, failureReason: response.statusText };
  }
}
